import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { inventoryItemActions } from "../slices/inventoryItem";
import { getErrorMessage } from "../../utils/errorUtils";
import { InventoryItemRequestDto } from "../../shared/dtos/inventoryItem/InventoryItemRequestDto";
import { InventoryItemResponseDto } from "../../shared/dtos/inventoryItem/InventoryItemResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getInventoryItems =
  (params: PaginationQueryDto = { page: 1, size: 10 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(inventoryItemActions.setLoadingFetchAll());
    try {
      const queryParams: Record<string, any> = {
        page: params.page,
        size: params.size,
      };
      if (params.search) queryParams.search = params.search;

      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<InventoryItemResponseDto>>
      >("/inventory-items", {
        headers: { requiresAuth: true },
        params: queryParams,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(inventoryItemActions.setItems(data.data.content));
        dispatch(inventoryItemActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(inventoryItemActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getInventoryItemById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(inventoryItemActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<InventoryItemResponseDto>
      >(`/inventory-items/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(inventoryItemActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(inventoryItemActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createInventoryItem =
  (values: InventoryItemRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(inventoryItemActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<InventoryItemResponseDto>
      >("/inventory-items", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Inventory item created successfully!");
        dispatch(inventoryItemActions.createSuccess(data.data));
        dispatch(
          inventoryItemActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(inventoryItemActions.setErrorCreate(message));
    }
  };

export const updateInventoryItem =
  (payload: { id: string; values: InventoryItemRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(inventoryItemActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<InventoryItemResponseDto>
      >(`/inventory-items/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Inventory item updated successfully!");
        dispatch(inventoryItemActions.updateSuccess(data.data));
        dispatch(
          inventoryItemActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(inventoryItemActions.setErrorUpdate(message));
    }
  };

export const deleteInventoryItem =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(inventoryItemActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/inventory-items/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Inventory item deleted successfully!");
        dispatch(inventoryItemActions.deleteSuccess(id));
        dispatch(
          inventoryItemActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(inventoryItemActions.setErrorDelete(message));
    }
  };

export const resetInventoryItemServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(inventoryItemActions.setServerStatus(null));
};
